import { useEffect, useRef, useState } from "react";
import {
	AudioLines,
	Copy,
	Mic,
	MicOff,
	Video,
	VideoOff,
	MonitorUp,
	PhoneOff,
	MessageSquare,
	Users,
	ShieldCheck,
	Disc,
	Send,
	Rocket,
} from "lucide-react";
import { MeetingMesh, type Remote } from "./mesh";
import { request, type RoomState, type Session } from "./api";
import { AgentTile } from "./AgentTile";
import { Stage } from "./heads";
import { runStandup } from "./standup";
import { KICKOFF_AGENDA, runKickoff } from "./kickoff";

function Media({
	stream,
	audio = false,
}: {
	stream?: MediaStream;
	audio?: boolean;
}) {
	const ref = useRef<HTMLVideoElement & HTMLAudioElement>(null);
	const [blocked, setBlocked] = useState(false);
	useEffect(() => {
		const el = ref.current;
		if (el && stream) {
			el.srcObject = stream;
			void el.play().catch(() => setBlocked(true));
		}
		return () => {
			if (el) el.srcObject = null;
		};
	}, [stream]);
	return (
		<>
			{audio ? (
				<audio ref={ref} autoPlay />
			) : (
				<video ref={ref} autoPlay muted playsInline />
			)}
			{blocked && audio && (
				<button
					onClick={() => void ref.current?.play().then(() => setBlocked(false))}
				>
					Enable meeting audio
				</button>
			)}
		</>
	);
}
// ponytail: browser-native voice processing. voiceIsolation is Chrome-only and
// ignored elsewhere; reach for RNNoise/WASM only if this proves insufficient.
const VOICE_AUDIO: MediaTrackConstraints = {
	echoCancellation: true,
	noiseSuppression: true,
	autoGainControl: true,
	voiceIsolation: true,
} as MediaTrackConstraints & { voiceIsolation: boolean };

export function Call({
	id,
	session,
	name,
	onLeave,
}: {
	id: string;
	session: Session;
	name: string;
	onLeave: () => void;
}) {
	const [room, setRoom] = useState<RoomState | null>(null);
	const [remotes, setRemotes] = useState<Remote[]>([]);
	const [warning, setWarning] = useState("");
	const [error, setError] = useState("");
	const [busy, setBusy] = useState(false);
	const [muted, setMuted] = useState(false);
	const [camera, setCamera] = useState<MediaStream>();
	const [screen, setScreen] = useState<MediaStream>();
	const [chat, setChat] = useState("");
	const [copied, setCopied] = useState(false);
	const [stage, setStage] = useState<Stage>();
	const [speaking, setSpeaking] = useState<string | null>(null);
	const [farmOwner, setFarmOwner] = useState(false);
	const [standup, setStandup] = useState("");
	const [farmAgents, setFarmAgents] = useState<{ slug: string; name: string }[]>([]);
	const [kickoff, setKickoff] = useState<{ title: string; brief: string; agenda: string[] } | null>(null);
	const mesh = useRef<MeetingMesh>();
	const mic = useRef<MediaStreamTrack>();
	const [now, setNow] = useState(Date.now());
	const onLeaveRef = useRef(onLeave);
	onLeaveRef.current = onLeave;
	useEffect(() => {
		const m = new MeetingMesh(id, session, name);
		mesh.current = m;
		const st = new Stage(id, session);
		st.onSpeaking = setSpeaking;
		setStage(st);
		let disposed = false;
		m.onState = (state) => {
			setRoom(state);
			st.take(state.speech ?? []);
		};
		m.onRemote = setRemotes;
		m.onWarning = setWarning;
		m.onEnded = () => {
			setError("This meeting has ended.");
			setRoom((r) => (r ? { ...r, endedAt: Date.now() } : r));
		};
		void (async () => {
			let track: MediaStreamTrack | undefined;
			try {
				const stream = await navigator.mediaDevices.getUserMedia({
					audio: VOICE_AUDIO,
					video: false,
				});
				track = stream.getAudioTracks()[0];
			} catch {
				setMuted(true);
				setWarning("Microphone unavailable. You can still listen and chat.");
			}
			if (disposed) {
				track?.stop();
				return;
			}
			mic.current = track;
			try {
				await m.start(track);
			} catch (e) {
				setError(e instanceof Error ? e.message : "Could not join");
			}
		})();
		const timer = setInterval(() => setNow(Date.now()), 1000);
		return () => {
			disposed = true;
			clearInterval(timer);
			st.close();
			void m.leave().catch(() => {});
		};
	}, [id, session, name]);
	useEffect(() => {
		request<{ owner: boolean; agents: { slug: string; name: string }[] }>("/api/v1/agents/farm", session)
			.then((farm) => {
				setFarmOwner(farm.owner);
				setFarmAgents(farm.agents ?? []);
			})
			.catch(() => {});
	}, [session]);
	async function act(fn: () => Promise<void>) {
		setBusy(true);
		setError("");
		try {
			await fn();
		} catch (e) {
			setError(e instanceof Error ? e.message : "Action failed");
		} finally {
			setBusy(false);
		}
	}
	async function toggleCamera() {
		const m = mesh.current!;
		if (camera) {
			await m.setTrack("camera", null);
			setCamera(undefined);
			await m.command({ type: "media", camera: false });
			return;
		}
		const stream = await navigator.mediaDevices.getUserMedia({
			video: {
				width: { ideal: 640 },
				height: { ideal: 360 },
				frameRate: { ideal: 15, max: 24 },
			},
			audio: false,
		});
		try {
			await m.command({ type: "media", camera: true });
			await m.setTrack("camera", stream.getVideoTracks()[0]);
			setCamera(stream);
		} catch (e) {
			stream.getTracks().forEach((t) => t.stop());
			void m.command({ type: "media", camera: false }).catch(() => {});
			throw e;
		}
	}
	async function stopScreen() {
		await mesh.current!.setTrack("screen", null);
		setScreen(undefined);
		await mesh.current!.command({ type: "media", screen: false });
	}
	async function toggleScreen() {
		if (screen) return stopScreen();
		const stream = await navigator.mediaDevices.getDisplayMedia({
			video: { frameRate: 10 },
			audio: false,
		});
		try {
			await mesh.current!.command({ type: "media", screen: true });
			const track = stream.getVideoTracks()[0];
			await mesh.current!.setTrack("screen", track);
			setScreen(stream);
			track.onended = () => void act(stopScreen);
		} catch (e) {
			stream.getTracks().forEach((t) => t.stop());
			throw e;
		}
	}
	const me = room?.peers.find((p) => p.id === mesh.current?.peerId);
	const people = room?.peers.filter((p) => !p.recorder) || [];
	const sharer = people.find((p) => p.screen);
	const shared =
		sharer?.id === me?.id
			? screen
			: remotes.find((r) => r.id === sharer?.id)?.screen;
	const elapsed = Math.max(
		0,
		Math.floor((now - (room?.startedAt || now)) / 1000),
	);
	return (
		<div className="call-shell">
			<header className="call-header">
				<div className="brand">
					<span className="brand-mark">
						<AudioLines size={21} />
					</span>
					Pioneer <span className="brand-light">Meet</span>
				</div>
				<div className="call-heading">
					<strong>{room?.title || "Joining your meeting…"}</strong>
					<span>
						{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")} ·{" "}
						{people.length}/6 people
					</span>
				</div>
				<button
					onClick={() =>
						void act(async () => {
							await navigator.clipboard.writeText(location.href);
							setCopied(true);
							setTimeout(() => setCopied(false), 2000);
						})
					}
				>
					<Copy size={15} />
					{copied ? "Copied" : "Copy invite"}
				</button>
				{farmOwner && stage && (
					<button
						title="All farm agents. Standup text is saved in company Office."
						disabled={!!standup && !/done|stopped/.test(standup)}
						onClick={() =>
							void runStandup(id, mesh.current!.peerId, session, stage, setStandup).catch((e) =>
								setStandup(`Standup stopped: ${e instanceof Error ? e.message : "failed"}`),
							)
						}
					>
						<Users size={15} />
						Standup
					</button>
				)}
				{farmOwner && stage && (
					<button
						disabled={!!standup && !/done|stopped/.test(standup)}
						onClick={() => setKickoff({ title: "", brief: "", agenda: KICKOFF_AGENDA })}
					>
						<Rocket size={15} />
						Kickoff
					</button>
				)}
				{standup && <span className="standup-status" role="status">{standup}</span>}
			</header>
			{kickoff && stage && (
				<form
					className="kickoff-form"
					onSubmit={(e) => {
						e.preventDefault();
						const plan = kickoff;
						setKickoff(null);
						void runKickoff(id, mesh.current!.peerId, session, stage, setStandup, plan).catch((err) =>
							setStandup(`Kickoff stopped: ${err instanceof Error ? err.message : "failed"}`),
						);
					}}
				>
					<h2>Project kickoff</h2>
					<label>
						Project
						<input required value={kickoff.title} onChange={(e) => setKickoff({ ...kickoff, title: e.target.value })} placeholder="Company Treasury" />
					</label>
					<label>
						Brief (paste from KB Projects/&lt;name&gt;/Brief)
						<textarea required rows={8} value={kickoff.brief} onChange={(e) => setKickoff({ ...kickoff, brief: e.target.value.slice(0, 6000) })} />
					</label>
					<fieldset>
						<legend>Agents on the agenda</legend>
						{farmAgents.map((a) => (
							<label key={a.slug} className="kickoff-agent">
								<input
									type="checkbox"
									checked={kickoff.agenda.includes(a.slug)}
									onChange={(e) =>
										setKickoff({ ...kickoff, agenda: e.target.checked ? [...kickoff.agenda, a.slug] : kickoff.agenda.filter((s) => s !== a.slug) })
									}
								/>
								{a.name}
							</label>
						))}
					</fieldset>
					<div className="kickoff-actions">
						<button type="submit" disabled={!kickoff.agenda.length}>Start kickoff</button>
						<button type="button" onClick={() => setKickoff(null)}>Cancel</button>
					</div>
				</form>
			)}
			<div
				className={`recording-banner ${room?.recorderOnline ? "online" : ""}`}
				role="status"
			>
				<Disc size={15} />
				{room && !room.company ? room.archiveCompany ? "Farm standup. Agent speech and chat are saved for the company. Human audio recording is off." : "Standalone meeting. Recording and transcripts are off." : room?.recorderOnline
					? `Office is capturing audio${room.videoRecording ? ", cameras and screen share" : ""}. Transcripts are private to company members.`
					: "Office recorder offline. Your meeting continues; audio and transcripts may have gaps."}
			</div>
			{(room?.archiveCompany || room?.company) && <div role="status" className="notice">
				{room.archive?.state === "saved" ? "Agent speech and chat saved in Office." : room.archive?.last_error
					? "Office delivery failed. Meeting text is saved here and will retry automatically."
					: "Meeting text saved here. Office delivery pending."}
				{" "}{room.recorderOnline ? "Audio transcription is handled separately by the recorder." : "Human audio is not being transcribed while the recorder is offline."}
			</div>}
			{room?.guestDeadline && (
				<div className="notice">
					{room?.company ? "No company members are here. This meeting ends in" : "This meeting ends in"}{" "}
					{Math.max(0, Math.ceil((room.guestDeadline - now) / 60000))} minutes
					unless a member returns.
				</div>
			)}
			{warning && (
				<div role="status" className="notice">
					{warning}
				</div>
			)}
			{error && (
				<div role="alert" className="error call-error">
					{error}
				</div>
			)}
			<main className="call-main">
				<section className="stage">
					<div className="stage-label">
						<span className="status-dot" />
						{sharer ? `${sharer.name} is sharing` : "IN THE ROOM"}
						<span>
							<ShieldCheck size={13} /> Wallet authenticated
						</span>
					</div>
					{sharer && (
						<div className="screen-tile">
							<Media stream={shared} />
						</div>
					)}
					<div className={`tiles ${sharer ? "with-screen" : ""}`}>
						{stage &&
							room?.agents?.map((seat) => (
								<AgentTile key={seat.slug} seat={seat} stage={stage} speaking={speaking === seat.slug} />
							))}
						{people.map((p) => {
							const local = p.id === me?.id;
							const remote = remotes.find((r) => r.id === p.id);
							return (
								<div className="person-tile" key={p.id}>
									{p.camera ? (
										<Media stream={local ? camera : remote?.camera} />
									) : (
										<div className="person-avatar">
											{p.name.slice(0, 2).toUpperCase()}
										</div>
									)}
									<div className="person-label">
										{p.name}
										{local ? " (you)" : ""}
										{p.isMember && <ShieldCheck size={13} />}
									</div>
									{!local && <Media stream={remote?.audio} audio />}
								</div>
							);
						})}
						{!people.length && (
							<div className="empty-room">
								<AudioLines size={42} />
								<h2>
									{error ? "Unable to enter the room" : "Connecting your room"}
								</h2>
								<p>Your wallet is your introduction.</p>
							</div>
						)}
					</div>
				</section>
				<aside className="chat-panel">
					<div className="chat-heading">
						<MessageSquare size={17} />
						<strong>Room chat</strong>
						<Users size={15} />
					</div>
					<div className="messages">
						{!room?.chat.length && (
							<div className="chat-empty">
								<MessageSquare size={25} />
								<p>
									A thought, a link,
									<br />a good place to start.
								</p>
							</div>
						)}
						{room?.chat.map((msg) => (
							<div className="message" key={msg.id}>
								<div>
									<strong>{msg.name}</strong>
									<time>
										{new Date(msg.at).toLocaleTimeString([], {
											hour: "2-digit",
											minute: "2-digit",
										})}
									</time>
								</div>
								<p>{msg.text}</p>
							</div>
						))}
					</div>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							void act(async () => {
								await mesh.current!.command({ type: "chat", text: chat });
								setChat("");
							});
						}}
					>
						<input
							aria-label="Chat message"
							placeholder="Message the room"
							maxLength={2000}
							value={chat}
							onChange={(e) => setChat(e.target.value)}
						/>
						<button
							aria-label="Send message"
							disabled={!chat.trim() || busy || !room || !!room.endedAt}
						>
							<Send size={17} />
						</button>
					</form>
					<small>{room?.company ? "Recording & transcripts live in Office." : "No recording or transcripts."}</small>
				</aside>
			</main>
			<footer className="call-controls">
				<div className="control-group">
					<button
						disabled={!room || !!room.endedAt}
						aria-label={muted ? "Unmute microphone" : "Mute microphone"}
						className={muted ? "off" : ""}
						onClick={() =>
							void act(async () => {
								if (!mic.current) {
									const stream = await navigator.mediaDevices.getUserMedia({
										audio: VOICE_AUDIO,
									});
									mic.current = stream.getAudioTracks()[0];
									await mesh.current!.setTrack("audio", mic.current);
									setMuted(false);
								} else {
									mic.current.enabled = muted;
									setMuted(!muted);
								}
							})
						}
					>
						{muted ? <MicOff /> : <Mic />}
					</button>
					<button
						disabled={busy || !room || !!room.endedAt}
						aria-label={camera ? "Turn camera off" : "Turn camera on"}
						onClick={() => void act(toggleCamera)}
					>
						{camera ? <Video /> : <VideoOff />}
					</button>
					<button
						disabled={busy || !room || !!room.endedAt}
						aria-label={screen ? "Stop sharing" : "Share screen"}
						className={screen ? "active" : ""}
						onClick={() => void act(toggleScreen)}
					>
						<MonitorUp />
					</button>
				</div>
				{room?.company && me?.isMember && (
					<button
						disabled={busy || !!room?.endedAt}
						className={room?.videoRecording ? "active" : ""}
						onClick={() =>
							void act(async () => {
								await mesh.current!.command({
									type: "recording",
									enabled: !room?.videoRecording,
								});
							})
						}
					>
						<Disc size={17} />
						{room?.videoRecording ? "Stop video recording" : "Record video"}
					</button>
				)}
				<button
					className="leave"
					onClick={() => {
						void mesh.current?.leave().catch(() => {});
						onLeaveRef.current();
					}}
				>
					<PhoneOff size={19} />
					Leave
				</button>
				{(me?.isMember || (!room?.company && room?.organizer === session.address)) && !room?.endedAt && (
					<button
						className="text-button"
						onClick={() =>
							void act(async () => {
								if (window.confirm("End this meeting for everyone?"))
									await mesh.current!.command({ type: "end" });
							})
						}
					>
						End for everyone
					</button>
				)}
			</footer>
		</div>
	);
}
